import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Save, Building2, CreditCard, User, Wallet, Info, Settings } from 'lucide-react';
import { useAccountsNew } from './hooks/useAccountsNew';
import { cn } from '@/utils/cn';

export function AccountsNewPage() {
  const navigate = useNavigate();
  const { formData, setFormData, isSaving, createAccount } = useAccountsNew();

  return (
    <div className="w-full flex-1 min-w-0 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 rounded-md border border-slate-100 bg-white p-3 shadow-sm">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('?tab=account')}
            className="flex h-10 w-10 items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50 active:scale-95 transition-all"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-lg font-bold text-slate-900">Thêm tài khoản</h1>
            <p className="text-sm text-slate-500">Thiết lập thông tin tài khoản thanh toán mới.</p>
          </div>
        </div>
        <button
          onClick={createAccount}
          disabled={isSaving || !formData.accountName || !formData.accountNumber}
          className="flex h-10 items-center gap-2 rounded-md bg-emerald-700 px-4 text-sm font-bold text-white shadow-md shadow-emerald-700/10 hover:bg-emerald-800 disabled:opacity-50 transition-all active:scale-95"
        >
          <Save className="h-4 w-4" />
          <span>{isSaving ? 'Đang lưu...' : 'Lưu tài khoản'}</span>
        </button>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        {/* Main Form */}
        <div className="md:col-span-2 space-y-4">
          <div className="rounded-md border border-slate-100 bg-white p-4 shadow-sm space-y-4">
            <div className="flex items-center gap-2 border-b border-slate-50 pb-2 text-slate-800 font-bold">
              <Building2 className="h-4 w-4 text-emerald-600" />
              <span>Thông tin ngân hàng</span>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2 space-y-1.5">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Tên gợi nhớ</label>
                <input
                  type="text"
                  placeholder="Ví dụ: Tài khoản chính Techcombank"
                  className="h-11 w-full rounded-md border border-slate-200 bg-slate-50/50 px-3 text-sm outline-none focus:border-emerald-500 focus:bg-white transition-all"
                  value={formData.accountName}
                  onChange={(e) => setFormData({ ...formData, accountName: e.target.value })}
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Ngân hàng</label>
                <input
                  type="text"
                  placeholder="Ví dụ: Techcombank"
                  className="h-11 w-full rounded-md border border-slate-200 bg-slate-50/50 px-3 text-sm outline-none focus:border-emerald-500 focus:bg-white transition-all"
                  value={formData.bankName}
                  onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Số tài khoản</label>
                <input
                  type="text"
                  placeholder="Nhập số tài khoản..."
                  className="h-11 w-full rounded-md border border-slate-200 bg-slate-50/50 px-3 text-sm font-mono outline-none focus:border-emerald-500 focus:bg-white transition-all"
                  value={formData.accountNumber}
                  onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value })}
                />
              </div>

              <div className="sm:col-span-2 space-y-1.5">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Chủ tài khoản</label>
                <input
                  type="text"
                  placeholder="Ví dụ: CONG TY TNHH LANDSCAPE"
                  className="h-11 w-full rounded-md border border-slate-200 bg-slate-50/50 px-3 text-sm outline-none focus:border-emerald-500 focus:bg-white transition-all uppercase"
                  value={formData.accountHolder}
                  onChange={(e) => setFormData({ ...formData, accountHolder: e.target.value.toUpperCase() })}
                />
              </div>
            </div>
          </div>

          <div className="rounded-md border border-slate-100 bg-white p-4 shadow-sm space-y-4">
            <div className="flex items-center gap-2 border-b border-slate-50 pb-2 text-slate-800 font-bold">
              <Wallet className="h-4 w-4 text-emerald-600" />
              <span>Số dư & Tiền tệ</span>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Số dư ban đầu</label>
                <input
                  type="number"
                  className="h-11 w-full rounded-md border border-slate-200 bg-slate-50/50 px-3 text-sm outline-none focus:border-emerald-500 focus:bg-white transition-all"
                  value={formData.initialBalance}
                  onChange={(e) => setFormData({ ...formData, initialBalance: Number(e.target.value) })}
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Loại tiền</label>
                <select
                  className="h-11 w-full rounded-md border border-slate-200 bg-slate-50 px-3 text-sm font-bold outline-none focus:border-emerald-500 transition-all"
                  value={formData.currency}
                  onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                >
                  <option value="VND">VND - Việt Nam Đồng</option>
                  <option value="USD">USD - Đô la Mỹ</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <div className="rounded-md border border-slate-100 bg-white p-4 shadow-sm space-y-4">
            <div className="flex items-center gap-2 border-b border-slate-50 pb-2 text-slate-800 font-bold">
              <Settings className="h-4 w-4 text-emerald-600" />
              <span>Cấu hình</span>
            </div>

            <div className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Trạng thái</label>
                <select
                  className="h-11 w-full rounded-md border border-slate-200 bg-slate-50 px-3 text-sm font-bold outline-none focus:border-emerald-500 transition-all"
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                >
                  <option value="active">Đang hoạt động</option>
                  <option value="inactive">Tạm ngưng</option>
                </select>
              </div>

              <div className="flex items-center gap-3 rounded-md bg-slate-50 p-3">
                <input
                  type="checkbox"
                  id="isDefault"
                  className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                  checked={formData.isDefault}
                  onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                />
                <label htmlFor="isDefault" className="text-xs font-bold text-slate-700 cursor-pointer">
                  Đặt làm tài khoản mặc định
                </label>
              </div>
            </div>
          </div>

          <div className="rounded-md border border-slate-100 bg-white p-4 shadow-sm space-y-2">
             <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Ghi chú</label>
             <textarea
               rows={3}
               placeholder="Nhập ghi chú thêm..."
               className="w-full rounded-md border border-slate-200 bg-slate-50/50 p-3 text-sm outline-none focus:border-emerald-500 focus:bg-white transition-all"
               value={formData.description}
               onChange={(e) => setFormData({ ...formData, description: e.target.value })}
             />
          </div>
        </div>
      </div>
    </div>
  );
}
